// Shared snapshot-date rules for Questrade imports. CommonJS so
// server/investments.js can require() it and CRA can import it — react-scripts 5
// forbids client imports from outside client/src, so this is the one place both
// sides can reach. Same reason, and the same file location, as ruleMatch.js.
//
// Both sides genuinely need this. The client runs it on every keystroke so the
// operator sees the problem while the date field is still open; the server runs
// it at write time because the client is not an authority on anything. Two
// copies would drift, and what they encode — which date this data belongs to —
// is the one thing in the whole investments feature that has to be right.
//
// ⚠️  NO DESTRUCTURING IN THIS FILE — not `const [y, m] = ...`, not `const {a} = ...`.
// Babel compiles destructuring in a CommonJS module by injecting @babel/runtime
// interop helpers at the top of the output, and webpack then stops inferring
// this module's exports entirely: every import of it, named OR default, fails
// the production build with "is not exported from './investmentDates'" while
// Node still require()s it perfectly. It cost a failed deploy to find. Index
// into the array instead, the way ymOf() does below. Same applies to ruleMatch.js,
// which works today only because it happens to contain no destructuring.

var MONTH_NAMES = ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'];

// 'YYYY-MM-01' → { y: 2026, m: 6 }. Split by hand rather than through Date():
// new Date('2026-06-01') is parsed as UTC midnight and renders as May 31 in
// every timezone west of Greenwich, which is every timezone this app runs in.
function ymOf(iso) {
  var parts = String(iso || '').split('-');
  return { y: Number(parts[0]), m: Number(parts[1]) };
}

function monthLabel(iso) {
  var ym = ymOf(iso);
  if (!ym.y || !ym.m || ym.m < 1 || ym.m > 12) return '';
  return MONTH_NAMES[ym.m - 1] + ' ' + ym.y;
}

function addMonth(iso) {
  var ym = ymOf(iso);
  if (!ym.y || !ym.m) return '';
  if (ym.m === 12) return (ym.y + 1) + '-01-01';
  return ym.y + '-' + String(ym.m + 1).padStart(2, '0') + '-01';
}

// "June or July 2026" rather than "June 2026 or July 2026" — the year is said
// once when both months share it. Across a year boundary both are named in full,
// because "December or January 2027" would date December to the wrong year.
function monthPair(iso) {
  var next = addMonth(iso);
  var sameYear = String(iso).slice(0, 4) === String(next).slice(0, 4);
  if (sameYear) return monthLabel(iso).split(' ')[0] + ' or ' + monthLabel(next);
  return monthLabel(iso) + ' or ' + monthLabel(next);
}

// Day 0 of the following month is the last day of this one, so February and
// leap years come out right without a table.
function lastDayOfMonth(iso) {
  var ym = ymOf(iso);
  if (!ym.y || !ym.m) return '';
  var day = new Date(Date.UTC(ym.y, ym.m, 0)).getUTCDate();
  return ym.y + '-' + String(ym.m).padStart(2, '0') + '-' + String(day).padStart(2, '0');
}

// The 12-month sheet ends at the last COMPLETE month, so the export itself was
// taken in the month after it. Month-end, because these are month-end pictures.
//
// Never today. Today can be months adrift from the data — these exports end at
// Jun 26 while the calendar reads August — and a date field that defaults to a
// value its own validator rejects is worse than one left blank.
function suggestSnapshotDate(months) {
  if (!months || !months.length) return null;
  return lastDayOfMonth(addMonth(months[months.length - 1]));
}

// Twelve contiguous months ending in the snapshot month or the one before it is
// the only shape a real export takes. Anything else means the operator's date is
// probably wrong, and importing it would skew the history in a way nothing
// downstream would ever flag.
//
// Returns an array of human-readable problems — empty means good. Every message
// names both dates, because "invalid date" tells the operator nothing about
// which way to move it.
function validateSnapshotDate(months, snapshotDate) {
  var problems = [];
  var d = String(snapshotDate || '').trim();

  if (!d) return ['Enter the date this export was taken.'];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return ['"' + d + '" is not a date in YYYY-MM-DD form.'];

  // A Summary-only upload has no month columns to check against. That is a
  // legitimate partial upload, not an error — the date simply cannot be
  // cross-checked, which is why the field defaults to the other file's date.
  if (!months || !months.length) return problems;

  for (var i = 1; i < months.length; i++) {
    var prev = ymOf(months[i - 1]);
    var cur = ymOf(months[i]);
    if (cur.y * 12 + cur.m !== prev.y * 12 + prev.m + 1) {
      problems.push(
        'The month columns in this export are not contiguous — ' + monthLabel(months[i - 1]) +
        ' is followed by ' + monthLabel(months[i]) +
        '. This is not a file Tally knows how to read; do not import it.'
      );
      break;
    }
  }

  var last = months[months.length - 1];
  var snap = ymOf(d);
  var lastYm = ymOf(last);
  var gap = (snap.y * 12 + snap.m) - (lastYm.y * 12 + lastYm.m);
  if (gap !== 0 && gap !== 1) {
    problems.push(
      "This export's data ends " + monthLabel(last) +
      ' — the snapshot date should be ' + monthPair(last) + '.'
    );
  }
  return problems;
}

module.exports = { monthLabel, monthPair, addMonth, lastDayOfMonth, suggestSnapshotDate, validateSnapshotDate };
